import React, { Component } from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'
import PropTypes from 'prop-types'

export function nameContextProvider(name) {
  return `${name}ContextProvider`
}

// provider subscriptions class
export class ContextProviderSubscriptions {
  constructor({ onSubscribe, onUnsubscribe, onUnsubscribeAll, onProviderUnmount } = {}) {
    this.listeners = []
    this.values = {}
    this.subscribe = this.subscribe.bind(this)
    this.unsubscribe = this.unsubscribe.bind(this)
    this.onSubscribe = onSubscribe
    this.onUnsubscribe = onUnsubscribe
    this.onUnsubscribeAll = onUnsubscribeAll
    this.onProviderUnmount = onProviderUnmount
  }
  updateValues(newValues) {
    // notify listeners
    if (this.listeners.length) {
      for (let i = 0; i < this.listeners.length; i++) {
        const listener = this.listeners[i]
        if (listener.subscribeOn) {
          const subscribedValues = {}
          let updated = false
          for (let i = 0; i < listener.subscribeOn.length; i++) {
            const key = listener.subscribeOn[i]
            if (!updated && newValues[key] != this.values[key]) {
              updated = true
            }
            subscribedValues[key] = newValues[key]
          }
          if (updated) {
            listener(subscribedValues)
          }
        } else {
          listener(newValues)
        }
      }
    }
    // set values
    this.values = newValues
  }
  subscribe(listener) {
    // add listener if it is not exists in listeners list
    if (!this.listeners.includes(listener)) {
      this.listeners.push(listener)
    }
    // send last values to listener
    listener(this.values)
    // create bound unsubscribe function
    const unsubscribe = function () {
      return this.unsubscribe(listener)
    }.bind(this)
    // notify onSubscribe callback
    if (this.onSubscribe) {
      this.onSubscribe({ listener, unsubscribe, listeners: this.listeners })
    }
    // return unsubscribe function
    return unsubscribe
  }
  unsubscribe(listener) {
    let unsubscribed = false
    const listenerIndex = this.listeners.indexOf(listener)
    if (listenerIndex >= 0) {
      this.listeners.splice(listenerIndex, 1)
      unsubscribed = true
    }
    // notify onUnsubscribe callback
    if (this.onUnsubscribe) {
      this.onUnsubscribe({ listener, unsubscribed, listeners: this.listeners })
    }
    // notify onUnsubscribeAll callback
    if (this.onUnsubscribeAll && !this.listeners.length) {
      this.onUnsubscribeAll()
    }
    // return unsubscribed status
    return unsubscribed
  }
  providerUnmount() {
    // unsubscribe all listeners
    this.listeners.forEach(this.unsubscribe)
    // notify onProviderUnmount callback
    if (this.onProviderUnmount) {
      this.onProviderUnmount()
    }
  }
}

export default function ContextProvider(options = {}) {
  const { name, propTypes, contextTypes, values, shouldUpdate, mapToProps } = options

  const propsMapper =
    'all' == mapToProps
      ? function (values) {
          return values
        }
      : Array.isArray(mapToProps)
      ? function (values) {
          return mapToProps.reduce(function (props, key) {
            props[key] = values[key]
            return props
          }, {})
        }
      : 'object' == typeof mapToProps
      ? function (values) {
          return Object.keys(mapToProps).reduce(function (props, key) {
            props[mapToProps[key]] = values[key]
            return props
          }, {})
        }
      : 'function' == typeof mapToProps
      ? mapToProps
      : null

  return function (ProviderComponent) {
    if (null == values || null == name) {
      // nothing to provide
      return ProviderComponent
    }

    // ProviderComponent display name
    const providerComponentDisplayName = ProviderComponent.displayName || ProviderComponent.name || 'Component'

    // ${name}ContextProvider
    const thisNameContextProvider = nameContextProvider(name)

    // HOC class
    class ContextProviderComponent extends Component {
      // context propTypes
      static propTypes = propTypes

      // ref to wrapped component
      static WrappedComponent = ProviderComponent

      // display name
      static displayName = `ContextProvider[${name}](${providerComponentDisplayName})`

      // ref to context provider options
      static ContextProviderOptions = options

      // put context subscriptions into context
      static childContextTypes = {
        [thisNameContextProvider]: PropTypes.instanceOf(ContextProviderSubscriptions)
      }
      getChildContext() {
        return {
          [thisNameContextProvider]: this.subscriptions
        }
      }

      // fetch context values in constructor
      constructor(props) {
        super(props)
        this.fetchContextValues = this.fetchContextValues.bind(this)
        this.saveWrappedRef = this.saveWrappedRef.bind(this)
        this.saveWrappedComponentRef = this.saveWrappedComponentRef.bind(this)
        this.subscriptions = new ContextProviderSubscriptions()
        this.fetchContextValues(props)
      }

      // fetch context values and (optionaly) map it to props
      fetchContextValues(props) {
        // fetch values
        const fetchedValues = values.call(this, props) || {}
        // notify subscriptions
        this.subscriptions.updateValues(fetchedValues)
        // check context types
        if ('development' == process.env.NODE_ENV && contextTypes) {
          PropTypes.checkPropTypes(
            contextTypes,
            fetchedValues,
            `ContextProvider[${name}]`,
            providerComponentDisplayName
          )
        }
        // map values to props
        if (propsMapper) {
          this.contextProps = propsMapper.call(this, fetchedValues)
        }
      }

      // update context on props update if needed
      shouldComponentUpdate(nextProps) {
        if (shouldUpdate) {
          if (shouldUpdate.call(this, nextProps, this.props)) {
            this.fetchContextValues(nextProps)
          }
        } else {
          this.fetchContextValues(nextProps)
        }
        return true
      }

      // unsubscribe on unmount
      componentWillUnmount() {
        this.subscriptions.providerUnmount()
      }

      // save references from ManagedRenderComponent
      saveWrappedRef(name, element) {
        this[`ref${name}`] = element
        if (this.props.saveRef) {
          this.props.saveRef(name, element)
        }
      }

      // save wrapped component ref
      saveWrappedComponentRef(component) {
        this.refWrappedComponent = component
      }

      // render ProviderComponent with component props and context props
      render() {
        return (
          <ProviderComponent
            saveRef={this.saveWrappedRef}
            ref={this.saveWrappedComponentRef}
            {...this.props}
            {...this.contextProps}
          />
        )
      }
    }

    // copy non React static members
    hoistNonReactStatics(ContextProviderComponent, ProviderComponent)

    // return wrapped component
    return ContextProviderComponent
  }
}
